// src/components/sdk/SdkLoginContent.tsx
import React from 'react';
import {
    Form,
    Input,
    Button,
    Card,
    Typography,
    Alert,
    Space,
    message,
} from 'antd';
import {
    UserOutlined,
    LockOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd/es/form';
import { sdkMgmt } from '../../server/SdkMgmt';
import { LoginFormData, LoginResponse, SdkLoginContentProps } from './SdkInterface';

const { Title, Text } = Typography;

interface SdkLoginContentState {
    loading: boolean;
    errorMessage: string;
}

class SdkLoginContent extends React.Component<SdkLoginContentProps, SdkLoginContentState> {
    formRef = React.createRef<FormInstance>();

    constructor(props: SdkLoginContentProps) {
        super(props);
        this.state = {
            loading: false,
            errorMessage: '',
        };
    }

    // 登录提交
    handleSubmit = async (values: LoginFormData) => {
        const DEBUG = true;
        const TAG = `SdkLoginContent.handleSubmit() - `;
        this.setState({ loading: true, errorMessage: '' });

        try {
            const { email, password, remember } = values;

            if (DEBUG) {
                console.log(TAG, `username:`, email);
                console.log(TAG, `password:`, password);
                console.log(TAG, `remember:`, remember);
            }

            let response = await sdkMgmt.login(email, password);
            DEBUG && console.log(TAG, `response:\n`, response);

            const result: LoginResponse = response;
            DEBUG && console.log(TAG, `result:\n`, result);

            if (result.success) {
                if (result.data) {
                    const token = result.data.token;
                    // 登录成功
                    const userData = {
                        token: token,
                        user: {
                            email: result.data.email,
                            name: result.data.email,
                            role: result.data.role,
                        },
                        apps: result.data.apps,
                    };
                    message.success('登录成功！');
                    this.props.onLoginSuccess?.(userData);
                }
            } else {
                const errMsg = result.message || '登录失败，请检查用户名和密码';
                this.setState({ errorMessage: errMsg });
                message.error('登录失败');
                this.props.onLoginFail?.({ message: errMsg });
            }
        } catch (error: any) {
            this.setState({ errorMessage: error.message || '网络错误，请稍后重试' });
            message.error('登录失败');
        } finally {
            this.setState({ loading: false });
        }
    };

    render() {
        const { trans, onNavigateToRegister } = this.props;
        const { loading, errorMessage } = this.state;

        return (
            <Card
                bordered={false}
                style={{
                    borderRadius: 16,
                    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                    padding: '8px 4px',
                }}
            >
                {/* Logo 区域 */}
                <div style={{ textAlign: 'center', marginBottom: 32, marginTop: 8 }}>
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: 14,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 16,
                        }}
                    >
                        <AppstoreOutlined style={{ fontSize: 28, color: '#fff' }} />
                    </div>
                    <Title level={3} style={{ marginBottom: 4 }}>
                        SDK 开发者平台
                    </Title>
                    <Text type="secondary">
                        登录您的开发者账号
                    </Text>
                </div>

                {errorMessage && (
                    <Alert
                        message={errorMessage}
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: 24 }}
                        onClose={() => this.setState({ errorMessage: '' })}
                    />
                )}

                <Form
                    ref={this.formRef}
                    name="login"
                    onFinish={this.handleSubmit}
                    layout="vertical"
                    size="large"
                    requiredMark={false}
                    initialValues={{
                        remember: true,
                    }}
                >
                    <Form.Item
                        name="email"
                        label="用户名"
                        rules={[
                            { required: true, message: '请输入用户名' },
                            { min: 2, message: '用户名长度不能少于2位' },
                        ]}
                    >
                        <Input
                            prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="请输入用户名"
                            autoComplete="username"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label="登录密码"
                        rules={[
                            { required: true, message: '请输入登录密码' },
                            { min: 6, message: '密码长度不能少于6位' },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="请输入登录密码"
                            autoComplete="current-password"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 16 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            loading={loading}
                            size="large"
                            style={{
                                height: 44,
                                borderRadius: 8,
                                fontSize: 16,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                            }}
                        >
                            {loading ? '登录中...' : '登 录'}
                        </Button>
                    </Form.Item>

                    <div style={{ textAlign: 'center' }}>
                        <Space>
                            <Text type="secondary">还没有账号？</Text>
                            <Button
                                type="link"
                                style={{ padding: 0, color: '#667eea' }}
                                onClick={onNavigateToRegister}
                            >
                                立即注册
                            </Button>
                        </Space>
                    </div>
                </Form>
            </Card>
        );
    }
}

export default SdkLoginContent;